import { useCallback, useMemo } from 'react'
import {
  AddressAutoComplete,
  type Option,
} from '~/components/admin/fields/property/AddressAutocomplete'
import { Button } from '~/components/ui'
import { useGeocodeSearch } from '~/hooks'

type MapAddressSearchFilterProps = {
  addressSearch?: string
  updateFilter: (updates: Record<string, any>) => void
  validCities?: string[]
  validCounties?: string[]
  validStates?: string[]
  validPostalCodes?: string[]
}

export default function MapAddressSearchFilter({
  addressSearch,
  updateFilter,
  validCities = [],
  validCounties = [],
  validStates = [],
  validPostalCodes = [],
}: MapAddressSearchFilterProps) {
  const {
    setSearch: setLocationSearch,
    predictions,
    setPredictions,
    getPlaceData,
  } = useGeocodeSearch()

  const options: Option[] = useMemo(
    () =>
      predictions.map((prediction) => ({
        value: prediction.place_id,
        label: prediction.description,
        description: prediction.description,
      })),
    [predictions]
  )

  const handleSelectOption = useCallback(
    async (option: Option) => {
      const prediction = predictions.find((p) => p.place_id === option.value)
      if (prediction) {
        const placeData = await getPlaceData(prediction)
        if (placeData !== null) {
          // Only fill in the address part, not the other location data
          const updates: Record<string, any> = {
            addressSearch: placeData.address,
            cities: [],
            counties: [],
            postalCodes: [],
            states: [],
          }

          if (placeData.city && placeData.city.trim()) {
            const cityName = placeData.city.trim()
            if (validCities.includes(cityName)) {
              updates.cities = [cityName]
            }
          }

          if (
            updates.cities.length === 0 &&
            placeData.county &&
            placeData.county.trim()
          ) {
            const countyName = placeData.county.replace('County', '').trim()
            if (validCounties.includes(countyName)) {
              updates.counties = [countyName]
            }
          }

          if (
            updates.cities.length === 0 &&
            updates.counties.length === 0 &&
            placeData.postalCode &&
            placeData.postalCode.trim()
          ) {
            const postalCode = placeData.postalCode.trim()
            if (validPostalCodes.includes(postalCode)) {
              updates.postalCodes = [postalCode]
            }
          }

          if (placeData.stateAbbr) {
            // Try state abbreviation first
            if (validStates.includes(placeData.stateAbbr)) {
              updates.states = [placeData.stateAbbr]
            }
          }

          updateFilter(updates)
        }
      }

      setPredictions([])
    },
    [
      predictions,
      getPlaceData,
      updateFilter,
      setPredictions,
      validCities,
      validCounties,
      validStates,
      validPostalCodes,
    ]
  )

  const handleInputChange = useCallback(
    (value: string) => {
      setLocationSearch(value)
      updateFilter({ addressSearch: value })
    },
    [setLocationSearch, updateFilter]
  )

  return (
    <div className="space-y-2">
      <p className="text-sm">
        Address Search
        {(addressSearch ?? '').length > 0 && (
          <Button
            variant="link"
            size="none"
            className="ml-2"
            onClick={() => {
              updateFilter({ addressSearch: undefined })
            }}>
            (reset)
          </Button>
        )}
      </p>
      <div className="relative">
        <AddressAutoComplete
          options={options}
          address={addressSearch ?? ''}
          onSelectOption={handleSelectOption}
          onInputChange={handleInputChange}
          placeholder="Search by address..."
        />
      </div>
    </div>
  )
}
